import React, { useEffect, useMemo, useState } from 'react';

import classNames from 'classnames';

import BudgetsTableRow from './BudgetsTableRow';
import CompareVersions from './CompareVersions';
import ComparisonSelector, { COMPARISON_MODE_PRIOR } from './ComparisonSelector';
import EditSnapshotModal from './EditSnapshotModal';
import { buildDualComparisons, orderedVersions, resolveReference } from './versionComparison';
import VersionHistoryDrawer from './VersionHistoryDrawer';
import VersionsHero from './VersionsHero';

import { combinePaginationUrl } from 'Helpers/urlService';

import PaginationInfo from 'CommonComponents/PaginationInfo';
import TablePagination from 'CommonComponents/TablePagination';

// The comparison dropdown, hero reference and shape deltas all re-base
// client-side across ALL versions, so the whole set must be in `budgets` state
// at once. Versions per event are few by domain design (created only by an
// explicit lock), so a single unbounded page is cheap and keeps re-basing
// instant with no reload — rather than paginating and only ever seeing 10.
const ALL_VERSIONS_PER_PAGE = 1000;

function BudgetsTable(props) {
    const [budgets, setBudgets] = useState([]);
    const [summary, setSummary] = useState(null);
    const [comparisonMode, setComparisonMode] = useState(COMPARISON_MODE_PRIOR);
    const [perPage, setPerPage] = useState(ALL_VERSIONS_PER_PAGE);
    const [page, setPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [to, setTo] = useState(1);
    const [from, setFrom] = useState(1);
    const [total, setTotal] = useState(1);
    const [currentUrl, setCurrentUrl] = useState('');

    // Compare mode (IB-757): a toggle turns the list into a two-version picker.
    // `selectedForCompare` holds the chosen budget ids, capped at exactly 2 — a
    // third pick drops the oldest. `comparison` is the fetched compare payload;
    // while it is set the Compare view replaces the list.
    const [compareMode, setCompareMode] = useState(false);
    const [selectedForCompare, setSelectedForCompare] = useState([]);
    const [comparison, setComparison] = useState(null);
    const [isComparing, setIsComparing] = useState(false);

    // IB-758: the version whose "Edit snapshot…" reason modal is open, and the
    // version whose history drawer is open. Only one of each at a time.
    const [editSnapshotBudget, setEditSnapshotBudget] = useState(null);
    const [historyBudget, setHistoryBudget] = useState(null);

    useEffect(() => {
        fetchBudgets();
        fetchSummary();
    }, []);

    // "+ New Version" reuses the fully-wired createBudgetVersion Blade modal that
    // the top-nav "Add" button already renders (inc/logo.blade.php →
    // event/create-budget-version). Whether it shows comes from a server flag
    // (props.canCreateVersion) that mirrors that partial's exact gating —
    // deterministic, unlike probing the DOM for the toggle which races the
    // server-rendered nav. When the flag is true the toggle is guaranteed
    // present (same gating), so clicking it opens the modal with all its AJAX
    // submit + validation logic reused.
    const openNewVersion = () => {
        document.querySelector('[data-modal-toggle="createBudgetVersion"]')?.click();
    };

    // The comparison dropdown re-bases every row's movement AND the hero
    // comparison tile entirely client-side from the versions already fetched, so
    // switching the reference is instant (no reload).
    const comparisons = useMemo(() => buildDualComparisons(budgets, comparisonMode), [budgets, comparisonMode]);
    const comparisonReference = useMemo(() => resolveReference(budgets, comparisonMode), [budgets, comparisonMode]);
    const versionOptions = useMemo(
        () => orderedVersions(budgets).map((budget) => ({ id: budget.id, version: budget.metrics.version, name: budget.metrics.name })),
        [budgets],
    );

    // The Actuals hero tile shows actuals as a % of the Latest Estimate's
    // expenditure. The summary payload carries the LE net but not its
    // expenditure, so resolve it client-side from the fetched versions.
    const latestEstimateExpenditure = useMemo(() => {
        if (!summary || !summary.latestEstimate) {
            return null;
        }

        const match = budgets.find((budget) => budget.metrics && budget.id === summary.latestEstimate.id);

        return match ? match.metrics.expenditure : null;
    }, [budgets, summary]);

    const fetchSummary = () => {
        if (!props.summaryUrl) {
            return;
        }

        axios
            .get(props.summaryUrl)
            .then((res) => setSummary(res.data))
            .catch((error) => {
                console.log(error);
            });
    };

    const defaultUrl = combinePaginationUrl(props.fetchUrl, perPage);

    const selectBudget = (budgetId) => {
        setBudgets(getUpdatedBudgets(budgets, budgetId));
    };

    const cancelBudgetSelected = () => {
        setBudgets(getUpdatedBudgets(budgets));
    };

    const combineFetchUrl = (p = null, pp = null) => {
        return combinePaginationUrl(props.fetchUrl, pp || perPage, p || page);
    };

    const getUpdatedBudgets = (data, budgetId = null) => {
        return data.map((budget) => (budgetId && budget.id === budgetId ? { ...budget, isSelected: true } : { ...budget, isSelected: false }));
    };

    const fetchBudgets = (url = null) => {
        const fetchUrl = url || defaultUrl;
        setCurrentUrl(fetchUrl);

        axios
            .get(fetchUrl)
            .then((res) => {
                const { data, meta } = res.data;
                const updatedBudgets = getUpdatedBudgets(data);
                setBudgets(updatedBudgets);
                setTo(meta.to);
                setFrom(meta.from);
                setTotal(meta.total);
                setLastPage(meta.last_page);
            })
            .catch((error) => {
                console.log(error);
            });
    };

    function pageChange(data) {
        const url = combineFetchUrl(data.selected + 1);
        setCurrentUrl(url);
        setPage(+data.selected + 1);
        fetchBudgets(url);
    }

    const getDetailsUrl = (budgetId) => {
        return props.detailsUrl.replace('budget_id', budgetId);
    };

    // Toggle compare mode on/off. Leaving compare mode clears any selection and
    // any fetched comparison so the list returns to its normal state.
    const toggleCompareMode = () => {
        setCompareMode((on) => !on);
        setSelectedForCompare([]);
        setComparison(null);
    };

    // Pick/unpick a version for comparison. Capped at exactly 2 — picking a third
    // drops the oldest (first) selection, mirroring the mockup's onPick.
    const toggleCompareSelection = (budgetId) => {
        setSelectedForCompare((current) => {
            if (current.includes(budgetId)) {
                return current.filter((id) => id !== budgetId);
            }

            const next = [...current, budgetId];

            return next.length > 2 ? next.slice(next.length - 2) : next;
        });
    };

    const runComparison = () => {
        if (!props.compareUrl || selectedForCompare.length !== 2 || isComparing) {
            return;
        }

        const [a, b] = selectedForCompare;
        setIsComparing(true);

        axios
            .get(props.compareUrl, { params: { a, b } })
            .then((res) => {
                setComparison(res.data);
                setIsComparing(false);
            })
            .catch((error) => {
                setIsComparing(false);
                console.log(error);
            });
    };

    const backToVersions = () => {
        setComparison(null);
    };

    // The Compare view replaces the whole list once a comparison is fetched.
    if (comparison) {
        return (
            <div className="table__card">
                <CompareVersions comparison={comparison} onBack={backToVersions} />
            </div>
        );
    }

    return (
        <div className="table__card">
            {/* Top-right action cluster: the comparison dropdown, the Compare
                toggle and the "+ New Version" button all sit above the hero,
                aligned right — moved here from the pagination row so the primary
                actions lead the surface. */}
            <div className="tw-mb-4 tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-3">
                {compareMode && <span className="tw-text-sm tw-text-charcoal-50">{$t('Select two versions')}</span>}
                {!compareMode && <ComparisonSelector versions={versionOptions} mode={comparisonMode} onChange={setComparisonMode} />}
                {props.compareUrl && (
                    <button
                        type="button"
                        className={classNames(
                            'tw-inline-flex tw-h-10 tw-items-center tw-gap-x-2 tw-rounded-lg tw-border tw-px-4 tw-text-sm tw-font-semibold',
                            compareMode
                                ? 'tw-border-charcoal-25 tw-bg-white tw-text-charcoal hover:tw-border-charcoal-50'
                                : 'tw-border-grey tw-bg-white tw-text-charcoal',
                        )}
                        onClick={toggleCompareMode}
                    >
                        {compareMode ? $t('Cancel') : $t('⇄ Compare')}
                    </button>
                )}
                {compareMode && (
                    <button
                        type="button"
                        className="tw-inline-flex tw-h-10 tw-items-center tw-gap-x-2 tw-rounded-lg tw-bg-blue tw-px-4 tw-text-sm tw-font-semibold tw-text-white disabled:tw-opacity-50"
                        disabled={selectedForCompare.length !== 2 || isComparing}
                        onClick={runComparison}
                    >
                        {$t('Compare selected →')}
                    </button>
                )}
                {props.canCreateVersion && !compareMode && (
                    <button
                        type="button"
                        className="tw-inline-flex tw-h-10 tw-items-center tw-gap-x-2 tw-rounded-lg tw-bg-blue tw-px-4 tw-text-sm tw-font-semibold tw-text-white"
                        onClick={openNewVersion}
                    >
                        {$t('+ New Version')}
                    </button>
                )}
            </div>
            <VersionsHero
                summary={summary}
                comparisonReference={comparisonReference}
                comparisonMode={comparisonMode}
                latestEstimateExpenditure={latestEstimateExpenditure}
            />
            <div className="table__pagination-info tw-mb-6">
                <PaginationInfo to={to} from={from} total={total} />
                <div className="tw-flex tw-items-center tw-gap-x-4">
                    <TablePagination lastPage={lastPage} pageChange={pageChange} />
                </div>
            </div>
            <div className="inbud-table_wrapper">
                <table className="inbud-table tw-w-full tw-table-fixed">
                    <thead>
                        <tr>
                            {compareMode && <th className="tw-w-10" />}
                            <th className="tw-w-[17%]">{$t('Version')}</th>
                            <th className="tw-w-[18%]">{$t('Revenue')}</th>
                            <th className="tw-w-[18%]">{$t('Expenditure')}</th>
                            <th className="tw-w-[18%]">{$t('Net position')}</th>
                            <th className="tw-w-[13%]">{$t('Shape of budget')}</th>
                            <th className="tw-w-[10%]">{$t('Created')}</th>
                            <th className="tw-w-[6%]" />
                        </tr>
                    </thead>
                    <tbody>
                        {budgets.map((budget, index) => (
                            <BudgetsTableRow
                                key={budget.id}
                                budget={budget}
                                comparison={comparisons[budget.id]}
                                getDetailsUrl={getDetailsUrl}
                                downloadUrl={props.downloadUrl}
                                updateUrl={props.updateUrl}
                                selectBudget={selectBudget}
                                cancelBudgetSelected={cancelBudgetSelected}
                                fetchBudgets={() => fetchBudgets(currentUrl)}
                                isEditable={(page === 1 && index != 0) || page > 1}
                                deleteAttachmentUrl={props.deleteAttachmentUrl}
                                compareMode={compareMode}
                                isCompareSelected={selectedForCompare.includes(budget.id)}
                                onToggleCompareSelection={toggleCompareSelection}
                                onEditSnapshot={setEditSnapshotBudget}
                                onOpenHistory={setHistoryBudget}
                            />
                        ))}
                    </tbody>
                </table>
            </div>

            {editSnapshotBudget && props.editSnapshotUrl && (
                <EditSnapshotModal budget={editSnapshotBudget} editUrl={props.editSnapshotUrl} onClose={() => setEditSnapshotBudget(null)} />
            )}
            {historyBudget && props.versionHistoryUrl && (
                <VersionHistoryDrawer budget={historyBudget} historyUrl={props.versionHistoryUrl} onClose={() => setHistoryBudget(null)} />
            )}
        </div>
    );
}

export default BudgetsTable;
